// Per-player affordances (spec FE-18): which of your own buttons may light right now, worked out
// by the same validate() that will judge the press, plus the cooldown deadlines. Built inside
// buildView, so it only ever says what that player may know:
// - every view carries every key in the same shape, whatever the roles at the table;
// - a target is only ever something already in this view (a keke in sight, a breakdown in sight,
//   your own stop, a fixed place on the map), so a target never reveals what the view hides;
// - the agbero-only parts (kill, sabotage) are zero in a driver's view and live only in the
//   agbero's own view, the same place `you.killReadyAt` already lives;
// - a knock on a keke an area boy shields still lights, as a press on it would only then find out.
import { getMap } from '@wahala/maps';
import { locked } from './arrest.js';
import { dist2 } from './geometry.js';
import { validate } from './match.js';
import { stopAt } from './rides.js';
import { rulesFor } from './rules.js';
import { checkStart } from './sabotage.js';
import { checkVent } from './vents.js';
export const AFFORDANCE_KEYS = [
    'task',
    'report',
    'button',
    'fix',
    'kill',
    'sabotage',
    'vent',
    'ride',
    'settle',
    'send',
    'cut',
    'pickup',
    'go',
];
const off = () => ({ may: false, now: false, id: null, at: 0 });
/** The nearest candidate that the check accepts. */
function nearest(from, list, at, ok) {
    let best = null;
    let bestD = Number.POSITIVE_INFINITY;
    for (const x of list) {
        const p = at(x);
        if (!p)
            continue;
        const d = dist2(from, p);
        if (d < bestD && ok(x)) {
            best = x;
            bestD = d;
        }
    }
    return best;
}
/** Reasons that only mean "not yet" or "not here": the button still belongs on the road. */
const WAITING = new Set(['COOLDOWN', 'OUT_OF_RANGE']);
/**
 * What this player's buttons may do, given the things this view already shows: the players it
 * shows in sight (by id, with a position) and the breakdowns it shows.
 */
export function affordancesFor(s, v, inSight, bodies) {
    const out = {
        task: off(),
        report: off(),
        button: off(),
        fix: off(),
        kill: off(),
        sabotage: off(),
        vent: off(),
        ride: off(),
        settle: off(),
        send: off(),
        cut: off(),
        pickup: off(),
        go: off(),
    };
    // the horn's cooldown is park-wide and already in every view
    out.button.at = s.meetingCooldownUntil;
    if (s.phase !== 'PLAYING' || v.departed)
        return out;
    // arrested or in the yard: no button at all
    if (locked(s, v.id))
        return out;
    const rules = rulesFor(s);
    const map = getMap(s.mapId);
    const free = v.vent === null;
    const refused = (t) => rules.refuse(s, v, t) !== null;
    const passes = (cmd, also = null) => {
        const r = validate(s, cmd);
        return r.ok || r.reason === also;
    };
    const pid = v.id;
    // your stops: any of yours not done (a later stage waits on its own clock); a stop already open
    // counts, as the phone resumes it
    const tasks = s.tasks[pid] ?? [];
    if (free && !refused('task.interact') && tasks.some((t) => !t.done)) {
        out.task.may = true;
        const t = nearest(v.pos, tasks, (x) => map.stations.find((st) => st.id === x.stationId)?.pos, (x) => passes({ t: 'task.interact', playerId: pid, taskId: x.id }, 'SESSION_OPEN'));
        if (t)
            Object.assign(out.task, { now: true, id: t.id });
    }
    if (v.alive && free && !refused('player.report')) {
        out.report.may = true;
        // only breakdowns this view shows: a body behind a wall is never named here
        const b = nearest(v.pos, bodies, (x) => x.pos, (x) => passes({ t: 'player.report', playerId: pid, bodyId: x.id }));
        if (b)
            Object.assign(out.report, { now: true, id: b.id });
    }
    {
        const r = validate(s, { t: 'player.button', playerId: pid });
        out.button.may = r.ok || WAITING.has(r.reason);
        out.button.now = r.ok;
    }
    if (s.sabotage && v.alive && free && !refused('sabotage.fix')) {
        out.fix.may = true;
        const ids = map.fixes[s.sabotage.kind];
        const st = nearest(v.pos, map.stations.filter((x) => ids.includes(x.id)), (x) => x.pos, (x) => passes({ t: 'sabotage.fix', playerId: pid, stationId: x.id }));
        if (st)
            Object.assign(out.fix, { now: true, id: st.id });
    }
    if (v.role === 'impostor') {
        out.kill.at = v.killReadyAt;
        out.sabotage.at = s.sabotageReadyAt ?? 0;
        if (v.alive && free && !refused('player.kill')) {
            out.kill.may = true;
            // only kekes this view shows in sight, by their real ids (a disguise row is not a target);
            // a shield's refusal is the area boy's secret, so it does not dim the button
            const k = nearest(v.pos, inSight, (x) => x.pos, (x) => Object.hasOwn(s.players, x.id) && passes({ t: 'player.kill', playerId: pid, targetId: x.id }, 'PROTECTED'));
            if (k)
                Object.assign(out.kill, { now: true, id: k.id });
        }
        const r = refused('sabotage.start') ? 'NOT_IN_MODE' : checkStart(s, v, 'nepa');
        out.sabotage.may = r === null || r === 'COOLDOWN';
        out.sabotage.now = r === null;
    }
    if (s.rides && v.alive && free && !refused('ride.stop')) {
        out.ride.may = true;
        const st = stopAt(map, v.pos);
        if (st && validate(s, { t: 'ride.stop', playerId: pid }).ok)
            Object.assign(out.ride, { now: true, id: st.id });
    }
    const boys = s.areaBoys;
    if (boys) {
        const prompt = boys.prompt[pid];
        if (prompt && validate(s, { t: 'boys.answer', playerId: pid, choice: 'settle' }).ok)
            Object.assign(out.settle, { may: true, now: true, id: prompt.boy });
        if (v.role === 'impostor' && v.alive && free && (boys.sends[pid] ?? 0) > 0) {
            out.send.may = true;
            // only kekes this view shows in sight, by their real ids
            const k = nearest(v.pos, inSight, (x) => x.pos, (x) => Object.hasOwn(s.players, x.id) && passes({ t: 'boys.send', playerId: pid, targetId: x.id }));
            if (k)
                Object.assign(out.send, { now: true, id: k.id });
        }
    }
    // the yard's padlock: in reach of the gate while someone is locked up
    const ar = s.arrest;
    if (ar && v.alive && free && Object.keys(ar.jailed).length > 0) {
        out.cut.may = true;
        const r = validate(s, { t: 'breakout.cut', playerId: pid });
        const cut = Object.hasOwn(ar.cuts, pid) ? ar.cuts[pid] : undefined;
        if (cut)
            out.cut.at = cut.at + (s.config.arrest?.cutMs ?? 0);
        // between cuts the disc waits on `at`; it names the yard only when a cut may go in now
        if (r.ok)
            Object.assign(out.cut, { now: true, id: 'yard' });
    }
    // the mode's own buttons (a race: your pickup disc and Go at the pump)
    rules.extendAffordances?.(s, v, out);
    if (free && !refused('vent.enter')) {
        const first = map.vents[0];
        const probe = first ? checkVent(s, v, 'enter', first.id, rules.mayVent(s, v, 'enter')) : 'BAD_TARGET';
        if (probe === null || WAITING.has(probe)) {
            out.vent.may = true;
            out.vent.at = v.ventCooldownUntil;
            const x = nearest(v.pos, map.vents, (y) => y.pos, (y) => passes({ t: 'vent.enter', playerId: pid, ventId: y.id }));
            if (x)
                Object.assign(out.vent, { now: true, id: x.id });
        }
    }
    return out;
}
//# sourceMappingURL=affordances.js.map